/**
 * useVoiceMode — encapsulates Whisper STT (mic) and ElevenLabs TTS (speaker)
 * for the Sourcing AI Agent panel.
 *
 *   const { recording, recStartStop, ttsBusy, playReply, voiceOn, toggleVoice }
 *     = useVoiceMode({ onTranscript: (text) => setInput(text) });
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';

const VOICE_PREF_KEY = 'cognara_voice_mode';

export function useVoiceMode({ onTranscript, agent, voiceOverride }) {
  const [recording, setRecording] = useState(false);
  const [voiceOn, setVoiceOn] = useState(
    () => localStorage.getItem(VOICE_PREF_KEY) === '1'
  );
  const [ttsBusy, setTtsBusy] = useState(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const audioElRef = useRef(null);

  // Persist voice-on preference
  useEffect(() => {
    localStorage.setItem(VOICE_PREF_KEY, voiceOn ? '1' : '0');
  }, [voiceOn]);

  const toggleVoice = useCallback(() => {
    setVoiceOn((v) => {
      if (v && audioElRef.current) audioElRef.current.pause();
      return !v;
    });
  }, []);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data?.size && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        if (blob.size < 1000) {
          toast.warning('Recording too short — hold the mic and speak.');
          setRecording(false);
          return;
        }
        const fd = new FormData();
        fd.append('file', blob, 'voice.webm');
        try {
          const r = await api.post('/voice/transcribe', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          if (r.data?.text) onTranscript?.(r.data.text);
          else toast.info('No speech detected.');
        } catch (e) {
          toast.error(e?.response?.data?.detail || 'Transcription failed');
        } finally {
          setRecording(false);
        }
      };
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
    } catch (e) {
      toast.error('Mic permission denied or unavailable');
      setRecording(false);
    }
  }, [onTranscript]);

  const stopRecording = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
    }
  }, []);

  const recStartStop = useCallback(() => {
    recording ? stopRecording() : startRecording();
  }, [recording, startRecording, stopRecording]);

  // Auto-play TTS for any agent reply when voiceOn is true.
  const playReply = useCallback(async (text) => {
    if (!voiceOn || !text) return;
    // Strip markdown for cleaner speech.
    const clean = text
      .replace(/[#*_`~|]+/g, ' ')
      .replace(/\[[^\]]+\]\([^)]+\)/g, '$1')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 1200); // keep TTS latency low for first-pass
    if (!clean) return;
    setTtsBusy(true);
    try {
      const r = await api.post('/voice/tts', {
        text: clean, agent, voice: voiceOverride || undefined,
      });
      if (audioElRef.current) {
        audioElRef.current.pause();
        audioElRef.current = null;
      }
      const audio = new Audio(r.data.audio_url);
      audioElRef.current = audio;
      audio.onended = () => setTtsBusy(false);
      audio.onerror = () => setTtsBusy(false);
      await audio.play();
    } catch (e) {
      setTtsBusy(false);
      const msg = e?.response?.data?.detail || 'TTS failed';
      toast.error(typeof msg === 'string' ? msg.slice(0, 200) : 'TTS failed');
    }
  }, [voiceOn]);

  return { recording, recStartStop, voiceOn, toggleVoice, ttsBusy, playReply };
}
